import { CommonUtils } from './CommonUtils';
import { getEntityDisplayLabel, getEntityRouteParam } from './entityIdentifier';

let lastBreadcrumbMeta = null;

const recordDisplayName = (entity, record, displayId) => {
  const primaryFieldName = record?.metadata?.primary_field_name ?? CommonUtils.getPrimaryField(entity)?.name;
  const primaryValue = primaryFieldName ? record?.data?.[primaryFieldName] : undefined;
  return primaryValue || record?.display_id || displayId || '';
};

const recordDisplayId = (record, displayId) => record?.display_id ?? displayId ?? '';

const normalizeResourcePath = resource => {
  if (!resource) {
    return '/schemas';
  }
  return String(resource).split('?')[0].replace(/\/+$/, '') || '/schemas';
};

/**
 * list: `/schemas`, schema: create/edit, object: records list, record: record show.
 */
export const classifyNativeUiResource = resource => {
  const path = normalizeResourcePath(resource);
  if (/^\/schemas\/[^/]+\/records\/[^/]+$/.test(path)) {
    return 'record';
  }
  if (/^\/schemas\/[^/]+\/records$/.test(path)) {
    return 'object';
  }
  if (path === '/schemas/create' || /^\/schemas\/edit\/[^/]+$/.test(path)) {
    return 'schema';
  }
  return 'list';
};

/**
 * Remember object/record breadcrumb meta across in-iframe navigations.
 * List/create/edit clear it. Object/record emits reuse the last named object when meta is omitted
 * so a later ROUTE_CHANGE without meta cannot wipe the host breadcrumbs.
 */
export const resolveRouteChangeMeta = (resource, meta) => {
  const page = classifyNativeUiResource(resource);
  if (page === 'list' || page === 'schema') {
    lastBreadcrumbMeta = null;
    return null;
  }
  if (meta?.object) {
    if (meta.object.name) {
      lastBreadcrumbMeta = meta;
    }
    return meta;
  }
  return lastBreadcrumbMeta;
};

export const resetLastRouteChangeMeta = () => {
  lastBreadcrumbMeta = null;
};

/**
 * Builds the ROUTE_CHANGE `meta` payload the host uses for breadcrumbs.
 * `object.path` / `record.path` are CO UI paths (no ROUTE_PREFIX).
 */
export const buildRouteChangeMeta = ({ entity, record, routeParam, displayId } = {}) => {
  const entityId = getEntityRouteParam(entity) ?? routeParam;
  const meta = {
    object: {
      name: getEntityDisplayLabel(entity) ?? '',
      id: entityId != null ? String(entityId) : '',
      path: entityId != null && entityId !== '' ? `/schemas/${entityId}/records` : '',
    },
  };

  if (record || displayId != null) {
    const recordId = recordDisplayId(record, displayId);
    meta.record = {
      name: String(recordDisplayName(entity, record, displayId)),
      id: String(recordId),
      path: entityId != null && entityId !== '' && recordId !== '' ? `/schemas/${entityId}/records/${recordId}` : '',
    };
  }

  return meta;
};
